import { h, inject } from 'vue';
import { checkElementViewportFit } from '@/Utils';
import { adjustFullWidth } from '@/Utils/BoundaryUtility';
import { ToggleInjectionKey } from './ToggleContainer';

export default {
    name: 'ToggleTrigger',
    emits: ['click', 'collapse', 'dragenter', 'dragleave', 'dragover', 'drop', 'expand'],
    props: {
        activeClass: { type: String, default: 'is-active' },
        inactiveClass: { type: String, default: 'is-inactive' },
        autoCollapse: { type: Boolean, default: true },
        collapseOnScroll: { type: Boolean, default: false },
        fixedContainer: { type: Boolean, default: false },
        handleClick: { type: Boolean, default: true },
        focusChildren: { type: Boolean, default: false },
        tag: { type: String, default: 'div' },
        ensureContainerPosition: { type: Boolean, default: false },
        containerFullScreenWidth: { type: Boolean, default: false },
        beforeActive: { type: Function, default: () => Promise.resolve() },
    },
    setup() {
        const toggle = inject(ToggleInjectionKey, null);

        return { toggle };
    },
    computed: {
        isActive() {
            return this.toggle ? this.toggle.state.isActive : false;
        },
        containerEl() {
            return this.toggle?.state.containerEl ?? null;
        },
    },
    methods: {
        onFocusOut(event) {
            if (!this.autoCollapse)
                return;

            event.preventDefault();

            if (event.relatedTarget) {
                if (this.$el.contains(event.relatedTarget) || this.containerEl?.contains(event.relatedTarget)) {
                    event.relatedTarget.addEventListener('focusout', this.onFocusOut.bind(this), { once: true });
                    return;
                }
            }

            this.collapse();
        },
        collapse() {
            if (this.isActive) {
                this.toggle?.collapse();
                this.$emit('collapse');
            }
        },
        stopListeningAndCollapse() {
            this.removeEventOnScroll();
            this.collapse();
        },
        addEventOnScroll() {
            window.addEventListener('scroll', this.stopListeningAndCollapse, true);
        },
        removeEventOnScroll() {
            window.removeEventListener('scroll', this.stopListeningAndCollapse, true);
        },
        expand() {
            if (!this.containerEl) {
                return;
            }

            this.ensureContentPosition();
            this.makeFullWidth();
            if (this.collapseOnScroll) {
                this.addEventOnScroll();
            }
            this.toggle.expand();

            if (this.autoCollapse) {
                if (this.focusChildren) {
                    this.$el.children[0].focus();
                }
                else {
                    this.$el.focus();
                }
            }

            this.$emit('expand');
        },
        ensureContentPosition() {
            if (this.fixedContainer || this.ensureContainerPosition) {
                const triggerBoundingRect = this.$el.parentElement.getBoundingClientRect();
                const screenWidth = window.innerWidth > 0 ? window.innerWidth : screen.width;
                const element = this.containerEl;

                element.style.left = 'unset';
                element.style.right = 'unset';

                const left = triggerBoundingRect.left;
                // Avoid sticking the container to the edge of the screen
                if (!checkElementViewportFit(element).right) {
                    element.style.right = `${ screenWidth - this.$el.offsetWidth - left }px`;
                }
                else {
                    // check if container fits on the screen
                    if (left + element.offsetWidth <= screenWidth) {
                        element.style.left = `${ left > 0 ? left : 0 }px`;
                    }
                    else {
                        element.style.left = `${ screenWidth - element.offsetWidth }px`;
                    }
                }

                if (this.fixedContainer) {
                    element.style.position = 'fixed';
                    element.style.top = `${ triggerBoundingRect.bottom }px`;
                }
            }
        },
        makeFullWidth() {
            adjustFullWidth(
                this.containerFullScreenWidth,
                this.fixedContainer,
                this.containerEl,
                this.$el,
            );
        },
    },
    render() {
        return h(this.tag, {
            class: {
                [this.activeClass]: this.isActive,
                [this.inactiveClass]: !this.isActive,
            },
            onDragover: event => {
                event.preventDefault(); this.$emit('dragover', event);
            },
            onDragenter: event => {
                event.preventDefault(); this.$emit('dragenter', event);
            },
            onDragleave: event => {
                event.preventDefault(); this.$emit('dragleave', event);
            },
            onDrop: event => {
                event.preventDefault(); this.$emit('drop', event);
            },
            onClick: event => {
                Promise.resolve()
                    .then(() => {
                        if (this.handleClick) {
                            event.preventDefault();
                            event.stopPropagation();

                            if (this.isActive) {
                                this.collapse();
                            }
                            else {
                                return this.beforeActive()
                                    .then(this.expand);
                            }
                        }

                        return Promise.resolve();
                    })
                    .then(() => {
                        this.$emit('click', event);
                    });
            },
            ...this.autoCollapse ? { onFocusout: this.onFocusOut } : {},
        }, this.$slots.default?.());
    },
    beforeUnmount() {
        if (this.collapseOnScroll) {
            this.removeEventOnScroll();
            this.collapse();
        }
    },
};
